import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, MessageCircle, Flame } from 'lucide-react';
import { PLATFORMS, BRAND } from '../data/mock';
import { resolveImg, createOrder } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import PlatformLogo from './PlatformLogo';

export default function CartDrawer() {
  const { items, setQty, remove, clear, total, count, open, setOpen } = useCart();
  const { user } = useAuth();
  const { toast } = useToast();
  const [checkout, setCheckout] = useState(false);
  const [customer, setCustomer] = useState({ name: '', phone: '', address: '' });
  const [busy, setBusy] = useState(false);

  // Prefill checkout details for logged-in customers (faster checkout)
  useEffect(() => {
    if (user) setCustomer(c => ({ name: c.name || user.name || '', phone: c.phone || user.phone || '', address: c.address || user.address || '' }));
  }, [user]);

  const submitOrder = async (platform) => {
    if (!items.length) return;
    setBusy(true);
    try {
      await createOrder({
        items: items.map(i => ({ name: i.name, price: i.price, qty: i.qty })),
        total,
        platform,
        customer
      });
      if (platform === 'whatsapp') {
        const lines = items.map(i => `• ${i.name} x${i.qty} — ₹${i.price * i.qty}`).join('%0A');
        const text = `*Zayka Sphere Order*%0A${lines}%0A%0A*Total:* ₹${total}%0A%0AName: ${customer.name || '-'}%0APhone: ${customer.phone || '-'}%0AAddress: ${customer.address || '-'}`;
        const url = `https://wa.me/919369050284?text=${text}`;
        window.open(url, '_blank');
      } else {
        const p = PLATFORMS.find(p => p.name.toLowerCase() === platform);
        toast({ title: `Opening ${p?.name || platform}…`, description: 'Redirecting to the delivery platform.' });
        if (p?.url) window.open(p.url, '_blank');
      }
      clear();
      setCheckout(false);
      setOpen(false);
      toast({ title: 'Order placed!', description: 'Thanks for choosing Zayka Sphere.' });
    } catch (e) {
      toast({ title: 'Something went wrong', description: e?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-neutral-950 border-l border-orange-500/20 text-white p-0">
          <div className="flex flex-col h-full">
            <SheetHeader className="px-6 py-5 border-b border-white/10">
              <SheetTitle className="font-display text-3xl tracking-wider text-white flex items-center gap-2">
                <ShoppingBag className="w-6 h-6 text-orange-500" /> YOUR CART
                <span className="ml-auto text-sm font-normal text-white/60">{count} item{count !== 1 ? 's' : ''}</span>
              </SheetTitle>
            </SheetHeader>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {items.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center py-16">
                  <div className="w-20 h-20 rounded-full bg-orange-500/10 border border-orange-500/40 flex items-center justify-center mb-4">
                    <ShoppingBag className="w-8 h-8 text-orange-500" />
                  </div>
                  <div className="font-bebas text-2xl">Your cart is empty</div>
                  <p className="text-white/50 text-sm mt-1">Add some crazy food to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map(it => (
                    <div key={it.name} className="flex gap-3 bg-neutral-900 border border-white/10 rounded-xl p-3">
                      <div className="w-16 h-16 rounded-lg overflow-hidden shrink-0">
                        <img src={resolveImg(it.image_url)} alt={it.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{it.name}</div>
                        <div className="text-orange-500 font-bebas text-xl">₹{it.price}</div>
                      </div>
                      <div className="flex flex-col items-end justify-between">
                        <button onClick={() => remove(it.name)} className="text-white/50 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                        <div className="flex items-center gap-1 bg-black/50 border border-white/10 rounded-full">
                          <button onClick={() => setQty(it.name, it.qty - 1)} className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-orange-400"><Minus className="w-3 h-3" /></button>
                          <span className="w-5 text-center text-sm font-semibold">{it.qty}</span>
                          <button onClick={() => setQty(it.name, it.qty + 1)} className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-orange-400"><Plus className="w-3 h-3" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 p-6 space-y-4 bg-neutral-950">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 uppercase text-xs tracking-widest">Subtotal</span>
                  <span className="font-display text-3xl text-orange-500">₹{total}</span>
                </div>
                <Button onClick={() => setCheckout(true)} className="w-full h-12 rounded-full bg-orange-500 hover:bg-orange-600 font-bold uppercase tracking-widest">
                  Checkout <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <button onClick={clear} className="w-full text-xs text-white/50 hover:text-red-400 uppercase tracking-widest">Clear cart</button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={checkout} onOpenChange={setCheckout}>
        <DialogContent className="bg-neutral-950 border border-orange-500/30 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-wider">
              CHOOSE <span className="text-orange-500">CHECKOUT</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60 -mt-2">Total <span className="text-orange-500 font-bold">₹{total}</span> — pick where to place your order.</p>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <Input placeholder="Your name (optional)" value={customer.name} onChange={e => setCustomer(c => ({ ...c, name: e.target.value }))} className="h-11 bg-black/50 border-white/10" />
            <Input placeholder="Phone (optional)" value={customer.phone} onChange={e => setCustomer(c => ({ ...c, phone: e.target.value }))} className="h-11 bg-black/50 border-white/10" />
          </div>
          <Input data-testid="checkout-address" placeholder="Delivery address (optional)" value={customer.address} onChange={e => setCustomer(c => ({ ...c, address: e.target.value }))} className="h-11 bg-black/50 border-white/10" />
          <div className="space-y-2 pt-2">
            <Button disabled={busy} onClick={() => submitOrder('whatsapp')} className="w-full h-12 rounded-xl bg-green-600 hover:bg-green-700 font-bold uppercase tracking-widest justify-start px-5">
              <MessageCircle className="w-5 h-5 mr-3" /> Order via WhatsApp
            </Button>
            {PLATFORMS.map(p => (
              <Button key={p.name} disabled={busy} onClick={() => submitOrder(p.name.toLowerCase())} variant="outline" className="w-full h-12 rounded-xl border-white/15 hover:border-orange-500 hover:bg-orange-500/10 justify-start px-5 bg-neutral-900 text-white">
                <span className="mr-3"><PlatformLogo platform={p} size={32} rounded="rounded-lg" /></span>
                <span className="font-bebas text-xl tracking-wide">Order on {p.name}</span>
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 text-[11px] text-white/40 pt-2">
            <Flame className="w-3 h-3 text-orange-500" /> Platform links open your delivery app or website.
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
